import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsArchivedLicense, selectLicenseDetails } from '../duck/selectors/license';
import { State } from 'model/State';
import config from 'appconfig';
import {
  StyledButtonsWrapper,
  StyledFlex,
  StyledLink,
  StyledTableData,
  StyledTextLabel,
} from './licensesExpandedRow/StyledComponents';
import TextArea from '@atlaskit/textarea';
import styled from 'styled-components';
import { DetailsRow } from './licensesExpandedRow/DetailsRowContent';
import { FormattedMessage } from 'react-intl';
import { messages } from '../messages';
import {
  addOnProductTypes,
  serverIdNotRequiredForKeys,
  serverIdRequiredForProductTypes,
} from 'util/mac/helpers';
import { StyledPipe } from './licensesExpandedRow/ActionsSectionElements/StyledPipe';
import { ActionsMessage } from './licensesExpandedRow/ActionsSectionElements/ActionsMessage';
import { fetchGenericProperty } from '../duck/actions/genericProperty';
import {
  getMACUserChannel,
  selectGenericPropertyByFullKey,
  selectHasSourceDownloads,
} from '../duck/selectors';
import { fetchHasSourceDownloads } from '../duck/actions/downloads';
import { DownloadSourceLink } from './licensesExpandedRow/ActionsSectionElements/DownloadSourceLink';
import { ArchiveUnarchiveButtonLink } from './licensesExpandedRow/ActionsSectionElements/ArchiveUnarchiveButtonLink';
import { Dimensions } from 'components/visuals';
import Spinner from '@atlaskit/spinner';
import { RESET_ARCHIVE_UNARCHIVE_LICENSE } from '../duck/actions/license';

const TextAreaWrapper = styled.div`
  width: ${Dimensions.units(28.75)}px;
`;

const SpinnerWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const TrialLicenseExpandedRow = ({ licenseId, isArchived }) => {
  const dispatch = useDispatch();

  const [genericPropertyFullKey, setGenericPropertyFullKey] = useState<string>('');
  const [keysList, setKeysList] = useState<string[]>([]);
  const [showSourceDownloadBtn, setShowSourceDownloadBtn] = useState(false);

  const { isPartner } = useSelector((state: State) => getMACUserChannel(state));
  const licenseDetails = useSelector((state) => selectLicenseDetails(state as State, licenseId));
  const genericPropertyTextValue = useSelector((state) =>
    selectGenericPropertyByFullKey(state as State, genericPropertyFullKey),
  );
  const hasSourceDownloads = useSelector(selectHasSourceDownloads);
  const isLicenseSuccessfullyArchived = useSelector(selectIsArchivedLicense);

  const details = licenseDetails?.licenseDetails;
  const packageDetails = licenseDetails?.productPackageDetails;

  const shouldShowViewLicenseLink =
    !isPartner &&
    !details?.sid &&
    (details?.description?.includes('Evaluation') || details?.description?.includes('Personal')) &&
    serverIdRequiredForProductTypes.includes(packageDetails?.productType) &&
    !serverIdNotRequiredForKeys.includes(packageDetails?.key);

  const displayBuyLink = packageDetails?.key && packageDetails?.publiclyAvailable;
  const productKey = packageDetails?.key?.replace('pricingplan.', '');

  const downloadLink = () => {
    let link: string = '';
    if (
      serverIdRequiredForProductTypes.includes(packageDetails?.productType) &&
      packageDetails?.downloadProduct?.downloadUrl
    ) {
      link = packageDetails?.downloadProduct?.downloadUrl;
    } else if (addOnProductTypes.includes(packageDetails?.productType)) {
      const key = packageDetails.key.replace('pricingplan.', '');
      link = `https://marketplace.atlassian.com/plugins/${key}`;
    }
    return link;
  };

  const downloadText = () => {
    let text: React.ReactNode;
    if (
      serverIdRequiredForProductTypes.includes(packageDetails?.productType) &&
      packageDetails?.downloadProduct?.downloadUrl
    ) {
      text = (
        <FormattedMessage
          {...messages.downloadWithDescription}
          values={{ description: packageDetails?.description }}
        />
      );
    } else if (addOnProductTypes.includes(packageDetails?.productType)) {
      text = <FormattedMessage id="mac.licenses.download" defaultMessage="Download" />;
    }
    return text;
  };

  useEffect(() => {
    if (packageDetails?.downloadProduct?.key && details?.licenseType === 'DEVELOPER') {
      setGenericPropertyFullKey(`related.source.product.${packageDetails.downloadProduct.key}`);
      dispatch(fetchGenericProperty(genericPropertyFullKey));
    }
  }, [packageDetails, genericPropertyFullKey]);

  useEffect(() => {
    if (genericPropertyTextValue && details?.licenseType === 'DEVELOPER') {
      const keys: string[] = [...genericPropertyTextValue.map(({ textValue }) => textValue)];
      setKeysList(keys);

      genericPropertyTextValue?.forEach((genericPropertyItem) => {
        dispatch(fetchHasSourceDownloads(genericPropertyItem.textValue));
      });
    }
  }, [genericPropertyTextValue]);

  useEffect(() => {
    if (hasSourceDownloads && keysList && details?.licenseType === 'DEVELOPER') {
      keysList.forEach((key) => {
        if (hasSourceDownloads[key] && hasSourceDownloads[key]._result === true) {
          setShowSourceDownloadBtn(true);
        }
      });
    }
  }, [hasSourceDownloads, keysList]);

  useEffect(() => {
    if (isLicenseSuccessfullyArchived === '') {
      window.location.href = `${config.root}/my`;
      dispatch({
        type: RESET_ARCHIVE_UNARCHIVE_LICENSE,
      });
    }
  }, [isLicenseSuccessfullyArchived]);

  return (
    <>
      {details ? (
        <>
          <StyledTableData colSpan={3}>
            {details?.sid && (
              <DetailsRow
                label={<FormattedMessage {...messages.serverIdLabel} />}
                value={details?.sid}
              />
            )}
            <DetailsRow
              label={<FormattedMessage {...messages.SEN} />}
              value={details?.id ? `SEN-${details?.id}` : ''}
            />
          </StyledTableData>
          <StyledTableData colSpan={2}>
            <StyledFlex>
              <StyledTextLabel>
                <FormattedMessage id="mac.licenses.license.key" defaultMessage="License key" />
              </StyledTextLabel>
              {shouldShowViewLicenseLink ? (
                <StyledLink href={`/purchase/my/license/license/${details?.id}`}>
                  <FormattedMessage id="mac.licenses.view.license" defaultMessage="View license" />
                </StyledLink>
              ) : (
                <TextAreaWrapper>
                  <TextArea id="area" maxHeight="20vh" value={details?.license} readOnly />
                </TextAreaWrapper>
              )}
            </StyledFlex>
            <StyledFlex>
              <ActionsMessage />
              <StyledButtonsWrapper>
                {details?.licenseType === 'DEVELOPER' && (
                  <>
                    <>
                      <StyledLink href={downloadLink()}>{downloadText()}</StyledLink>
                      <StyledPipe />
                    </>
                    {showSourceDownloadBtn && (
                      <DownloadSourceLink productKey={packageDetails?.downloadProduct?.key} />
                    )}
                  </>
                )}
                {details?.evaluation && (
                  <>
                    {displayBuyLink && (
                      <>
                        <StyledLink href={`/purchase/purchasetag/${productKey}`}>
                          <FormattedMessage {...messages.buy} />
                        </StyledLink>
                        <StyledPipe />
                      </>
                    )}
                    {downloadLink() && downloadText() && (
                      <>
                        <StyledLink href={downloadLink()}>{downloadText()}</StyledLink>
                        <StyledPipe />
                      </>
                    )}
                    {packageDetails?.marketplaceAddOn && (
                      <>
                        <StyledLink href={`/addon/try/${packageDetails?.key}`}>
                          <FormattedMessage {...messages.licenceEvaluationPageTitle} />
                        </StyledLink>
                        <StyledPipe />
                      </>
                    )}
                  </>
                )}
                <ArchiveUnarchiveButtonLink
                  isArchiveButtonVisible
                  isArchived={isArchived}
                  accountId={licenseId}
                  isAccount={false}
                />
              </StyledButtonsWrapper>
            </StyledFlex>
          </StyledTableData>
        </>
      ) : (
        <StyledTableData colSpan={5}>
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        </StyledTableData>
      )}
    </>
  );
};

export default TrialLicenseExpandedRow;
